import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Card, Button, Row, Col, Form, Input, Select, Slider, Radio, Typography,
  Space, Divider, message, Tag, Statistic, Badge, Switch, Tooltip,
} from 'antd';
import {
  RocketOutlined, ThunderboltOutlined, CrownOutlined, CheckCircleFilled,
  GlobalOutlined, VideoCameraOutlined, LineChartOutlined, RobotOutlined,
  CustomerServiceOutlined, ShareAltOutlined, ArrowRightOutlined,
  StarFilled, TeamOutlined, DollarOutlined, BarChartOutlined,
  ClockCircleOutlined, TranslationOutlined, SafetyCertificateOutlined,
  SendOutlined, HeartOutlined, TrophyOutlined, FireOutlined,
  MailOutlined, PhoneOutlined, HomeOutlined, UserOutlined,
  EnvironmentOutlined, CheckOutlined, CloseOutlined,
} from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// ── i18n translations ──────────────────────────────────────────────
const translations = {
  en: {
    nav: { howItWorks: 'How It Works', capabilities: 'AI Capabilities', pricing: 'Pricing', calculator: 'Calculator', testimonials: 'Partners', signUp: 'Sign Up' },
    hero: {
      title: 'AI is Your New Sales Team',
      subtitle: 'Partner with HOUSE AI and unlock a revenue-sharing model that grows as you grow. Our AI works 24/7 so you can close more deals, in more languages, in less time.',
      cta: 'Become a Partner',
      stats: { partners: 'Partners', countries: 'Countries', sales: 'in AI-Assisted Sales' },
    },
    howItWorks: {
      title: 'How It Works',
      subtitle: 'Four simple steps to transform your real estate business',
      steps: [
        { title: 'You Sign Up', desc: 'Choose your partnership tier and complete onboarding in under 10 minutes.' },
        { title: 'We Deploy AI', desc: 'Our team configures your dedicated AI agents, chatbots, and marketing tools.' },
        { title: 'AI Generates Leads & Sales', desc: 'AI works around the clock — answering inquiries, creating content, and nurturing prospects.' },
        { title: 'You Earn, We Earn', desc: 'Revenue-sharing means our success is tied to yours. We only win when you win.' },
      ],
    },
    capabilities: {
      title: 'What AI Does For You',
      subtitle: 'Six powerful AI modules working together to supercharge your business',
      items: [
        { title: '24/7 Multilingual Concierge', desc: 'AI chatbot handles buyer inquiries in 10+ languages, never sleeps, never misses a lead.' },
        { title: 'AI Video Tours', desc: 'Stunning property videos with AI narration in any language — generated in minutes, not days.' },
        { title: 'Smart Lead Scoring', desc: 'AI analyzes buyer behavior and intent signals to rank leads by likelihood to close.' },
        { title: 'Auto Marketing Engine', desc: 'AI creates and posts property listings, social content, and email campaigns automatically.' },
        { title: 'Post-Sale Service', desc: 'Automated satisfaction surveys, maintenance reminders, and relationship management.' },
        { title: 'Referral Engine', desc: 'AI identifies happy clients and automates the referral process to grow your pipeline.' },
      ],
    },
    pricing: {
      title: 'Partnership Tiers',
      subtitle: 'Choose the plan that matches your ambition',
      monthly: 'Monthly',
      annual: 'Annual',
      annualSave: 'Save 2 months',
      perMonth: '/mo',
      getStarted: 'Get Started',
      contactSales: 'Contact Sales',
      bestFor: 'Best for',
      revenueModel: 'Revenue model',
      tiers: {
        starter: {
          badge: 'STARTER',
          name: 'AI Marketing Partner',
          bestFor: 'Agents who want AI marketing but handle sales themselves',
          revenue: '$299/mo base + $50 per qualified lead generated by AI',
          features: [
            'AI-powered marketing content generation',
            '24/7 AI chatbot for buyer inquiries (10+ languages)',
            'AI video property tours with multilingual narration',
            'Facebook + Instagram auto-posting',
            'Monthly performance analytics report',
          ],
        },
        popular: {
          badge: 'MOST POPULAR',
          name: 'AI Sales Partner',
          bestFor: 'Agents who want AI to handle the entire sales cycle',
          revenue: '$799/mo base + 3% commission on closed deals',
          tag: 'Comboware invests in YOUR growth',
          features: [
            'Everything in Marketing Partner',
            'Full AI sales pipeline automation',
            'Comboware invests $500/mo in Google & Facebook Ads for you',
            'AI post-sale customer service & surveys',
            'Repeat business & referral automation engine',
            'LINE / WhatsApp / WeChat AI bot integration',
          ],
        },
        elite: {
          badge: 'ELITE',
          name: 'AI Enterprise Partner',
          bestFor: 'Top agencies ready to dominate their market with AI',
          revenue: '$1,499/mo base + 2% commission on closed deals',
          features: [
            'Everything in Sales Partner',
            'Dedicated AI trained on YOUR portfolio & brand voice',
            'Custom AI video production (10 videos/mo with lip-sync)',
            'Comboware invests $2,000/mo in ads across all platforms',
            'Priority 24/7 human + AI support',
            'White-label option (your brand, powered by HOUSE AI)',
            'Quarterly business strategy review with Comboware team',
          ],
        },
      },
    },
    revshare: {
      badge: 'REVENUE SHARE',
      title: 'Full Revenue Share Partnership',
      subtitle: 'Zero ad cost risk. We invest everything. You sell. We split.',
      model: '100K THB/mo base + 50/50 profit split on every closed deal',
      description: 'Comboware provides the full AI platform, dedicated AI employees, all marketing & advertising costs (Facebook, Google, LINE). You focus 100% on closing deals. We share the profits equally.',
      yourGet: 'What You Get',
      weProvide: 'What We Provide',
      protections: 'Built-in Protections',
      partnerGets: [
        'Full AI employee working 24/7 for your business',
        'Complete marketing platform (content, video, chatbot)',
        'All advertising costs covered (Facebook, Google, LINE Ads)',
        'AI-powered lead generation & qualification',
        'Multilingual AI concierge (10+ languages)',
        'AI video tours with lip-sync narration',
        'Post-sale automation & customer management',
      ],
      protectionItems: [
        { icon: 'ladder', title: 'Tiered Commission', desc: '< ฿5M → 50/50 | ฿5-10M → 55/45 | > ฿10M → 60/40 (you earn more as you grow)' },
        { icon: 'track', title: 'AI Lead Tracking', desc: 'Every lead is tracked from first touch to close — full transparency for both parties' },
        { icon: 'audit', title: 'AI Follow-Up Audit', desc: 'AI independently contacts all leads to verify deal status — prevents unreported closings' },
        { icon: 'tail', title: '24-Month Tail Period', desc: 'Leads generated during partnership remain in revenue-share scope for 24 months' },
        { icon: 'penalty', title: '3x Penalty Clause', desc: 'Circumvention penalty = 3x the commission that should have been shared' },
        { icon: 'flow', title: 'Commission Flow Control', desc: 'Deal commissions flow through the platform with automatic 50/50 split' },
      ],
      cta: 'Apply for Revenue Share',
    },
    calculator: {
      title: 'Revenue Calculator',
      subtitle: 'See how much you could earn with HOUSE AI',
      deals: 'Deals Closed per Month',
      avgPrice: 'Average Deal Value',
      tier: 'Your Tier',
      projected: 'Projected Monthly Revenue',
      aiLeads: 'AI-Generated Leads/mo',
      costSavings: 'Marketing Cost Savings',
      roi: 'Estimated ROI',
    },
    comparison: {
      title: 'The AI Advantage',
      subtitle: 'See the difference HOUSE AI makes',
      without: 'Without AI',
      with: 'With HOUSE AI',
      metrics: [
        { label: 'Lead Response Time', without: '2-24 hours', with: 'Instant (< 3 sec)' },
        { label: 'Languages Supported', without: '1-2', with: '10+' },
        { label: 'Cost per Lead', without: '$50-150', with: '$8-25' },
        { label: 'Working Hours', without: '8-10 hrs/day', with: '24/7/365' },
        { label: 'Lead Conversion Rate', without: '2-3%', with: '8-12%' },
        { label: 'Content Created/Week', without: '3-5 posts', with: '30+ posts' },
      ],
    },
    testimonials: {
      title: 'What Our Partners Say',
      subtitle: 'Real results from real estate professionals worldwide',
      items: [
        { name: 'Sarah Mitchell', role: 'Luxury Agent, Beverly Hills', country: 'USA', quote: 'HOUSE AI transformed my business. I went from 3 deals a month to 11. The AI chatbot alone handles 60% of my inquiries while I sleep. The revenue-sharing model means Comboware is as invested in my success as I am.' },
        { name: 'Takeshi Yamamoto', role: 'Agency Director, Tokyo', country: 'Japan', quote: 'The multilingual AI concierge is a game-changer for our international clients. We now serve buyers from 15 countries without hiring a single translator. Our lead conversion rate tripled in 4 months.' },
        { name: 'Ana Lucia Ferreira', role: 'Broker, Sao Paulo', country: 'Brazil', quote: 'The AI video tours with Portuguese narration have been incredible. Properties sell 40% faster now. The Enterprise tier ad investment has given us 10x return on ad spend consistently.' },
      ],
    },
    form: {
      title: 'Start Your AI Partnership',
      subtitle: 'Fill out the form below and our team will reach out within 24 hours',
      name: 'Full Name',
      company: 'Company / Agency',
      email: 'Email Address',
      phone: 'Phone Number',
      country: 'Country',
      city: 'City',
      listings: 'Current Active Listings',
      monthlySales: 'Monthly Sales Volume',
      tier: 'Preferred Tier',
      message: 'Message (optional)',
      submit: 'Submit Partnership Inquiry',
      success: 'Thank you! We will contact you within 24 hours.',
      error: 'Something went wrong. Please try again or email ai@comboware.ai.',
      namePh: 'John Smith',
      companyPh: 'Your Agency Name',
      emailPh: 'you@example.com',
      phonePh: '+1 555 123 4567',
      cityPh: 'e.g. Los Angeles',
      listingsPh: 'e.g. 25',
      monthlySalesPh: 'e.g. 5',
      messagePh: 'Tell us about your goals...',
    },
    footer: {
      company: 'Comboware, Inc.',
      location: 'Silicon Valley, California, USA',
      rights: 'All rights reserved.',
      tagline: 'Powering the future of real estate with AI.',
    },
  },

  zh: {
    nav: { howItWorks: '運作方式', capabilities: 'AI 功能', pricing: '方案價格', calculator: '收益計算', testimonials: '合作夥伴', signUp: '立即加入' },
    hero: {
      title: 'AI 就是你的新業務團隊',
      subtitle: '與 HOUSE AI 合作，解鎖隨業務成長的收益共享模式。我們的 AI 全年無休為你開發客戶、促成交易，讓你事半功倍。',
      cta: '成為合作夥伴',
      stats: { partners: '合作夥伴', countries: '覆蓋國家', sales: 'AI 輔助成交額' },
    },
    howItWorks: {
      title: '運作方式',
      subtitle: '四個簡單步驟，讓 AI 改造你的房地產事業',
      steps: [
        { title: '你註冊加入', desc: '選擇合作方案，10 分鐘內完成註冊。' },
        { title: '我們部署 AI', desc: '團隊為你配置專屬 AI 機器人、聊天系統與行銷工具。' },
        { title: 'AI 開發客戶', desc: 'AI 全天候運作 — 回覆諮詢、生成內容、培育潛在買家。' },
        { title: '你賺錢，我們也賺', desc: '收益共享代表我們的成功與你緊密相連，你贏我們才贏。' },
      ],
    },
    capabilities: {
      title: 'AI 為你做什麼',
      subtitle: '六大 AI 模組協同運作，全面提升你的業務',
      items: [
        { title: '24/7 多語言接待', desc: 'AI 聊天機器人以 10 種以上語言處理買家諮詢，全天候不間斷。' },
        { title: 'AI 影片導覽', desc: '用任何語言自動生成精美房產影片，數分鐘而非數天完成。' },
        { title: '智慧客戶評分', desc: 'AI 分析買家行為與意向信號，按成交可能性排列優先順序。' },
        { title: '自動行銷引擎', desc: 'AI 自動建立及發布房源資訊、社群內容與 Email 行銷。' },
        { title: '售後服務', desc: '自動滿意度調查、維護提醒及客戶關係管理。' },
        { title: '轉介引擎', desc: 'AI 辨識滿意客戶，自動化轉介流程，擴大你的業務管道。' },
      ],
    },
    pricing: {
      title: '合作方案',
      subtitle: '選擇匹配你雄心的方案',
      monthly: '月繳',
      annual: '年繳',
      annualSave: '省 2 個月',
      perMonth: '/月',
      getStarted: '立即開始',
      contactSales: '聯繫業務',
      bestFor: '適合對象',
      revenueModel: '收益模式',
      tiers: {
        starter: {
          badge: '入門',
          name: 'AI 行銷夥伴',
          bestFor: '希望用 AI 行銷但自行負責銷售的經紀人',
          revenue: '月費 $299 + AI 每產生一個合格線索 $50',
          features: ['AI 行銷內容自動生成', '24/7 AI 聊天機器人（支援 10+ 語言）', 'AI 影片房產導覽（多語旁白）', 'Facebook + Instagram 自動發布', '每月績效分析報告'],
        },
        popular: {
          badge: '最受歡迎',
          name: 'AI 銷售夥伴',
          bestFor: '希望 AI 處理整個銷售流程的經紀人',
          revenue: '月費 $799 + 成交金額 3% 佣金',
          tag: 'Comboware 投資你的成長',
          features: ['行銷夥伴所有功能', '完整 AI 銷售管線自動化', 'Comboware 每月投入 $500 廣告費', 'AI 售後服務與滿意度調查', '回購與轉介自動化引擎', 'LINE / WhatsApp / WeChat AI 機器人'],
        },
        elite: {
          badge: '菁英',
          name: 'AI 企業夥伴',
          bestFor: '準備用 AI 主導市場的頂級仲介公司',
          revenue: '月費 $1,499 + 成交金額 2% 佣金（更低佣金回饋）',
          features: ['銷售夥伴所有功能', '專屬 AI 學習你的房源與品牌風格', '客製 AI 影片（每月 10 支含口型同步）', 'Comboware 每月投入 $2,000 全平台廣告', '24/7 真人 + AI 優先支援', '白牌選項（你的品牌，HOUSE AI 驅動）', '每季與 Comboware 團隊策略會議'],
        },
      },
    },
    revshare: {
      badge: '利潤共享',
      title: '全方位利潤共享合作',
      subtitle: '零廣告成本風險。我們投資一切。你負責銷售。利潤對半分。',
      model: '月費 10 萬 THB + 成交利潤 50/50 分潤',
      description: 'Comboware 提供完整 AI 平台、專屬 AI 員工、全部行銷與廣告投放成本（Facebook、Google、LINE）。你 100% 專注成交。利潤平分。',
      yourGet: '你獲得的',
      weProvide: '我們提供的',
      protections: '內建防護機制',
      partnerGets: [
        '全天候 24/7 AI 員工為你的業務服務',
        '完整行銷平台（內容、影片、聊天機器人）',
        '全部廣告費用由 Comboware 承擔（Facebook、Google、LINE）',
        'AI 線索開發與資格篩選',
        '多語言 AI 接待（10+ 語言）',
        'AI 影片導覽（含口型同步旁白）',
        '售後自動化與客戶關係管理',
      ],
      protectionItems: [
        { icon: 'ladder', title: '階梯式分潤', desc: '< 500萬 → 50/50｜500萬-1000萬 → 55/45｜> 1000萬 → 60/40（越賣越多，你拿越多）' },
        { icon: 'track', title: 'AI 線索追蹤', desc: '每個線索從首次接觸到成交全程追蹤 — 雙方完全透明' },
        { icon: 'audit', title: 'AI 回訪稽核', desc: 'AI 獨立聯繫所有線索確認交易狀態 — 防止未回報的成交' },
        { icon: 'tail', title: '24 個月尾巴條款', desc: '合作期間產生的線索，終止後 24 個月內成交仍需分潤' },
        { icon: 'penalty', title: '3 倍違約金', desc: '規避分潤的罰款 = 應分潤金額的 3 倍' },
        { icon: 'flow', title: '傭金流管控', desc: '成交傭金透過平台自動 50/50 分帳' },
      ],
      cta: '申請利潤共享合作',
    },
    calculator: {
      title: '收益計算機',
      subtitle: '看看你能用 HOUSE AI 賺多少',
      deals: '每月成交筆數',
      avgPrice: '平均成交金額',
      tier: '你的方案',
      projected: '預估月收益',
      aiLeads: 'AI 每月產生線索',
      costSavings: '行銷成本節省',
      roi: '預估投資報酬率',
    },
    comparison: {
      title: 'AI 優勢對比',
      subtitle: '看看 HOUSE AI 帶來的差異',
      without: '沒有 AI',
      with: '有 HOUSE AI',
      metrics: [
        { label: '客戶回應時間', without: '2-24 小時', with: '即時（< 3 秒）' },
        { label: '支援語言數', without: '1-2 種', with: '10 種以上' },
        { label: '每條線索成本', without: '$50-150', with: '$8-25' },
        { label: '工作時間', without: '每天 8-10 小時', with: '24/7/365' },
        { label: '線索轉換率', without: '2-3%', with: '8-12%' },
        { label: '每週產出內容', without: '3-5 篇', with: '30 篇以上' },
      ],
    },
    testimonials: {
      title: '合作夥伴心聲',
      subtitle: '來自全球房地產專業人士的真實成果',
      items: [
        { name: 'Sarah Mitchell', role: '奢華房產經紀人，比佛利山莊', country: '美國', quote: 'HOUSE AI 徹底改變了我的事業。我從每月 3 筆成交增加到 11 筆。光是 AI 聊天機器人就在我睡覺時處理了 60% 的諮詢。' },
        { name: '山本剛', role: '仲介總監，東京', country: '日本', quote: '多語言 AI 接待對我們的國際客戶來說是翻天覆地的改變。我們現在服務 15 個國家的買家，不需要聘請翻譯。' },
        { name: 'Ana Lucia Ferreira', role: '經紀人，聖保羅', country: '巴西', quote: '帶有葡萄牙語旁白的 AI 影片導覽令人驚嘆。房產現在快 40% 售出。企業方案的廣告投資持續帶來 10 倍回報。' },
      ],
    },
    form: {
      title: '開啟你的 AI 合作',
      subtitle: '填寫以下表單，我們的團隊將在 24 小時內與你聯繫',
      name: '姓名',
      company: '公司 / 仲介',
      email: 'Email',
      phone: '電話',
      country: '國家',
      city: '城市',
      listings: '目前活躍房源數',
      monthlySales: '每月銷售量',
      tier: '偏好方案',
      message: '訊息（選填）',
      submit: '提交合作申請',
      success: '感謝！我們將在 24 小時內與您聯繫。',
      error: '發生錯誤，請重試或寄信至 ai@comboware.ai。',
      namePh: '王大明',
      companyPh: '你的公司名稱',
      emailPh: 'you@example.com',
      phonePh: '+886 912 345 678',
      cityPh: '例如：台北',
      listingsPh: '例如：25',
      monthlySalesPh: '例如：5',
      messagePh: '告訴我們你的目標...',
    },
    footer: {
      company: 'Comboware, Inc.',
      location: '美國加州矽谷',
      rights: '版權所有。',
      tagline: '以 AI 驅動房地產的未來。',
    },
  },

  th: {
    nav: { howItWorks: 'วิธีการทำงาน', capabilities: 'ความสามารถ AI', pricing: 'แพ็กเกจ', calculator: 'คำนวณรายได้', testimonials: 'พาร์ทเนอร์', signUp: 'สมัครเลย' },
    hero: {
      title: 'AI คือทีมขายใหม่ของคุณ',
      subtitle: 'ร่วมเป็นพาร์ทเนอร์กับ HOUSE AI และปลดล็อกโมเดลแบ่งปันรายได้ที่เติบโตไปพร้อมคุณ AI ของเราทำงาน 24 ชั่วโมงเพื่อให้คุณปิดดีลได้มากขึ้น',
      cta: 'เป็นพาร์ทเนอร์',
      stats: { partners: 'พาร์ทเนอร์', countries: 'ประเทศ', sales: 'ยอดขายที่ AI ช่วย' },
    },
    howItWorks: {
      title: 'วิธีการทำงาน',
      subtitle: 'สี่ขั้นตอนง่ายๆ เปลี่ยนธุรกิจอสังหาริมทรัพย์ของคุณ',
      steps: [
        { title: 'คุณสมัคร', desc: 'เลือกแพ็กเกจและลงทะเบียนภายใน 10 นาที' },
        { title: 'เราติดตั้ง AI', desc: 'ทีมของเราตั้งค่า AI เฉพาะ แชทบอท และเครื่องมือการตลาด' },
        { title: 'AI สร้างลีดและการขาย', desc: 'AI ทำงานตลอด 24 ชม. — ตอบคำถาม สร้างเนื้อหา และดูแลผู้สนใจ' },
        { title: 'คุณได้ เราก็ได้', desc: 'แบ่งปันรายได้หมายความว่าเราสำเร็จเมื่อคุณสำเร็จ' },
      ],
    },
    capabilities: {
      title: 'AI ทำอะไรให้คุณบ้าง',
      subtitle: 'หกโมดูล AI ที่ทำงานร่วมกันเพื่อเพิ่มพลังธุรกิจของคุณ',
      items: [
        { title: 'ต้อนรับหลายภาษา 24/7', desc: 'แชทบอท AI ตอบคำถามผู้ซื้อใน 10+ ภาษา ไม่เคยหลับ' },
        { title: 'ทัวร์วิดีโอ AI', desc: 'วิดีโอทรัพย์สินสวยงามพร้อมเสียงบรรยาย AI ในทุกภาษา' },
        { title: 'ให้คะแนนลีดอัจฉริยะ', desc: 'AI วิเคราะห์พฤติกรรมผู้ซื้อเพื่อจัดลำดับลีด' },
        { title: 'เครื่องยนต์การตลาดอัตโนมัติ', desc: 'AI สร้างและโพสต์เนื้อหาอัตโนมัติ' },
        { title: 'บริการหลังการขาย', desc: 'แบบสำรวจความพึงพอใจ การแจ้งเตือน และการจัดการลูกค้า' },
        { title: 'เครื่องยนต์การแนะนำ', desc: 'AI ระบุลูกค้าที่พอใจและอัตโนมัติกระบวนการแนะนำ' },
      ],
    },
    pricing: {
      title: 'แพ็กเกจพาร์ทเนอร์',
      subtitle: 'เลือกแผนที่ตรงกับเป้าหมายของคุณ',
      monthly: 'รายเดือน',
      annual: 'รายปี',
      annualSave: 'ประหยัด 2 เดือน',
      perMonth: '/เดือน',
      getStarted: 'เริ่มต้น',
      contactSales: 'ติดต่อฝ่ายขาย',
      bestFor: 'เหมาะสำหรับ',
      revenueModel: 'โมเดลรายได้',
      tiers: {
        starter: {
          badge: 'เริ่มต้น',
          name: 'AI Marketing Partner',
          bestFor: 'ตัวแทนที่ต้องการ AI ทำการตลาดแต่จัดการขายเอง',
          revenue: '$299/เดือน + $50 ต่อลีดที่มีคุณภาพ',
          features: ['สร้างเนื้อหาการตลาดด้วย AI', 'แชทบอท AI 24/7 (10+ ภาษา)', 'ทัวร์วิดีโอ AI หลายภาษา', 'โพสต์ Facebook + Instagram อัตโนมัติ', 'รายงานวิเคราะห์รายเดือน'],
        },
        popular: {
          badge: 'ยอดนิยม',
          name: 'AI Sales Partner',
          bestFor: 'ตัวแทนที่ต้องการให้ AI จัดการวงจรขายทั้งหมด',
          revenue: '$799/เดือน + 3% คอมมิชชั่น',
          tag: 'Comboware ลงทุนเพื่อการเติบโตของคุณ',
          features: ['ทุกอย่างใน Marketing Partner', 'ระบบขายอัตโนมัติด้วย AI', 'Comboware ลงทุน $500/เดือน ในโฆษณา', 'บริการหลังการขายด้วย AI', 'ระบบการซื้อซ้ำและการแนะนำ', 'LINE / WhatsApp / WeChat AI บอท'],
        },
        elite: {
          badge: 'ระดับสูง',
          name: 'AI Enterprise Partner',
          bestFor: 'เอเจนซี่ชั้นนำที่พร้อมครองตลาดด้วย AI',
          revenue: '$1,499/เดือน + 2% คอมมิชชั่น',
          features: ['ทุกอย่างใน Sales Partner', 'AI เฉพาะสำหรับพอร์ตและแบรนด์ของคุณ', 'วิดีโอ AI 10 ชิ้น/เดือน (lip-sync)', 'Comboware ลงทุน $2,000/เดือน ในโฆษณาทุกแพลตฟอร์ม', 'สนับสนุน 24/7 (คน + AI)', 'ตัวเลือก White-label', 'ประชุมกลยุทธ์รายไตรมาส'],
        },
      },
    },
    revshare: {
      badge: 'แบ่งปันรายได้',
      title: 'พาร์ทเนอร์แบ่งปันรายได้เต็มรูปแบบ',
      subtitle: 'ไม่มีความเสี่ยงค่าโฆษณา เราลงทุนทุกอย่าง คุณขาย เราแบ่งกำไร',
      model: '100K บาท/เดือน + แบ่งกำไร 50/50 ทุกดีลที่ปิด',
      description: 'Comboware จัดเตรียมแพลตฟอร์ม AI ครบวงจร พนักงาน AI เฉพาะ ค่าการตลาดและโฆษณาทั้งหมด (Facebook, Google, LINE) คุณโฟกัส 100% ที่การปิดดีล แบ่งกำไรเท่ากัน',
      yourGet: 'สิ่งที่คุณได้รับ',
      weProvide: 'สิ่งที่เราจัดเตรียม',
      protections: 'การป้องกันในตัว',
      partnerGets: [
        'พนักงาน AI ทำงาน 24/7 สำหรับธุรกิจของคุณ',
        'แพลตฟอร์มการตลาดครบวงจร (เนื้อหา, วิดีโอ, แชทบอท)',
        'ค่าโฆษณาทั้งหมด Comboware รับผิดชอบ (Facebook, Google, LINE)',
        'AI สร้างลีดและคัดกรองคุณภาพ',
        'AI ต้อนรับหลายภาษา (10+ ภาษา)',
        'ทัวร์วิดีโอ AI พร้อม lip-sync',
        'ระบบอัตโนมัติหลังการขายและจัดการลูกค้า',
      ],
      protectionItems: [
        { icon: 'ladder', title: 'คอมมิชชั่นแบบขั้นบันได', desc: '< ฿5M → 50/50 | ฿5-10M → 55/45 | > ฿10M → 60/40 (ยิ่งขายมาก ยิ่งได้มาก)' },
        { icon: 'track', title: 'AI ติดตามลีด', desc: 'ทุกลีดถูกติดตามตั้งแต่สัมผัสแรกจนปิดดีล — โปร่งใสทั้งสองฝ่าย' },
        { icon: 'audit', title: 'AI ตรวจสอบอิสระ', desc: 'AI ติดต่อลีดทั้งหมดเพื่อยืนยันสถานะดีล — ป้องกันการปิดดีลที่ไม่รายงาน' },
        { icon: 'tail', title: 'ข้อกำหนด 24 เดือน', desc: 'ลีดที่สร้างระหว่างพาร์ทเนอร์ชิพยังอยู่ในขอบเขตแบ่งรายได้ 24 เดือน' },
        { icon: 'penalty', title: 'ค่าปรับ 3 เท่า', desc: 'ค่าปรับการหลีกเลี่ยง = 3 เท่าของคอมมิชชั่นที่ควรแบ่ง' },
        { icon: 'flow', title: 'ควบคุมกระแสเงิน', desc: 'คอมมิชชั่นผ่านแพลตฟอร์มแบ่ง 50/50 อัตโนมัติ' },
      ],
      cta: 'สมัครพาร์ทเนอร์แบ่งรายได้',
    },
    calculator: {
      title: 'เครื่องคำนวณรายได้',
      subtitle: 'ดูว่าคุณจะได้เท่าไหร่กับ HOUSE AI',
      deals: 'ดีลที่ปิดต่อเดือน',
      avgPrice: 'มูลค่าดีลเฉลี่ย',
      tier: 'แพ็กเกจของคุณ',
      projected: 'รายได้ต่อเดือนที่คาดการณ์',
      aiLeads: 'ลีดจาก AI ต่อเดือน',
      costSavings: 'ประหยัดค่าการตลาด',
      roi: 'ROI ที่คาดการณ์',
    },
    comparison: {
      title: 'ข้อได้เปรียบของ AI',
      subtitle: 'ดูความแตกต่างที่ HOUSE AI สร้าง',
      without: 'ไม่มี AI',
      with: 'มี HOUSE AI',
      metrics: [
        { label: 'เวลาตอบลีด', without: '2-24 ชม.', with: 'ทันที (< 3 วิ)' },
        { label: 'ภาษาที่รองรับ', without: '1-2', with: '10+' },
        { label: 'ต้นทุนต่อลีด', without: '$50-150', with: '$8-25' },
        { label: 'ชั่วโมงทำงาน', without: '8-10 ชม./วัน', with: '24/7/365' },
        { label: 'อัตราแปลงลีด', without: '2-3%', with: '8-12%' },
        { label: 'เนื้อหาต่อสัปดาห์', without: '3-5 โพสต์', with: '30+ โพสต์' },
      ],
    },
    testimonials: {
      title: 'เสียงจากพาร์ทเนอร์',
      subtitle: 'ผลลัพธ์จริงจากมืออาชีพด้านอสังหาริมทรัพย์ทั่วโลก',
      items: [
        { name: 'Sarah Mitchell', role: 'ตัวแทนหรูหรา, เบเวอร์ลีฮิลส์', country: 'สหรัฐอเมริกา', quote: 'HOUSE AI เปลี่ยนธุรกิจของฉัน จาก 3 ดีลต่อเดือนเป็น 11 แชทบอท AI จัดการ 60% ของคำถามขณะที่ฉันนอนหลับ' },
        { name: 'Takeshi Yamamoto', role: 'ผู้อำนวยการ, โตเกียว', country: 'ญี่ปุ่น', quote: 'AI หลายภาษาเปลี่ยนเกมสำหรับลูกค้าต่างชาติ เราให้บริการผู้ซื้อจาก 15 ประเทศโดยไม่ต้องจ้างล่าม' },
        { name: 'Ana Lucia Ferreira', role: 'นายหน้า, เซาเปาลู', country: 'บราซิล', quote: 'ทัวร์วิดีโอ AI พร้อมเสียงภาษาโปรตุเกสน่าทึ่งมาก อสังหาริมทรัพย์ขายเร็วขึ้น 40% การลงทุนโฆษณาให้ผลตอบแทน 10 เท่า' },
      ],
    },
    form: {
      title: 'เริ่มพาร์ทเนอร์ชิพ AI ของคุณ',
      subtitle: 'กรอกแบบฟอร์มด้านล่าง ทีมของเราจะติดต่อภายใน 24 ชั่วโมง',
      name: 'ชื่อ-นามสกุล',
      company: 'บริษัท / เอเจนซี่',
      email: 'อีเมล',
      phone: 'โทรศัพท์',
      country: 'ประเทศ',
      city: 'เมือง',
      listings: 'จำนวนรายการอสังหาฯ ปัจจุบัน',
      monthlySales: 'ยอดขายต่อเดือน',
      tier: 'แพ็กเกจที่สนใจ',
      message: 'ข้อความ (ไม่บังคับ)',
      submit: 'ส่งใบสมัครพาร์ทเนอร์',
      success: 'ขอบคุณ! เราจะติดต่อคุณภายใน 24 ชั่วโมง',
      error: 'เกิดข้อผิดพลาด กรุณาลองใหม่หรืออีเมล ai@comboware.ai',
      namePh: 'สมชาย ใจดี',
      companyPh: 'ชื่อบริษัทของคุณ',
      emailPh: 'you@example.com',
      phonePh: '+66 81 234 5678',
      cityPh: 'เช่น กรุงเทพฯ',
      listingsPh: 'เช่น 25',
      monthlySalesPh: 'เช่น 5',
      messagePh: 'บอกเราเกี่ยวกับเป้าหมายของคุณ...',
    },
    footer: {
      company: 'Comboware, Inc.',
      location: 'ซิลิคอนวัลเลย์ แคลิฟอร์เนีย สหรัฐอเมริกา',
      rights: 'สงวนลิขสิทธิ์',
      tagline: 'ขับเคลื่อนอนาคตอสังหาริมทรัพย์ด้วย AI',
    },
  },
};

// ── Countries list ─────────────────────────────────────────────────
const countries = [
  'United States','Canada','United Kingdom','Australia','Japan','South Korea','Taiwan','Thailand',
  'Singapore','Malaysia','Indonesia','Philippines','Vietnam','India','China','Hong Kong','Germany',
  'France','Italy','Spain','Netherlands','Sweden','Switzerland','Brazil','Mexico','Colombia',
  'Argentina','Chile','UAE','Saudi Arabia','Israel','South Africa','Nigeria','Egypt','Turkey',
  'Portugal','New Zealand','Ireland','Poland','Czech Republic','Austria','Belgium','Denmark',
  'Norway','Finland','Greece','Other',
];

// ── API base ───────────────────────────────────────────────────────
const API_BASE = window.location.hostname === 'localhost'
  ? 'http://localhost:8000/api/v1'
  : '/api/v1';

// ── Keyframes (injected once) ──────────────────────────────────────
const STYLE_ID = 'partnership-page-styles';

function injectStyles() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes pp-float {
      0%, 100% { transform: translateY(0px); }
      50% { transform: translateY(-10px); }
    }
    @keyframes pp-shimmer {
      0% { background-position: -200% center; }
      100% { background-position: 200% center; }
    }
    @keyframes pp-pulse {
      0%, 100% { box-shadow: 0 0 0 0 rgba(212,160,60,0.4); }
      50% { box-shadow: 0 0 0 12px rgba(212,160,60,0); }
    }
    @keyframes pp-glow-border {
      0%, 100% { border-color: #D4A03C; box-shadow: 0 0 20px rgba(212,160,60,0.3), 0 8px 32px rgba(0,0,0,0.4); }
      50% { border-color: #f0c866; box-shadow: 0 0 40px rgba(212,160,60,0.5), 0 8px 32px rgba(0,0,0,0.4); }
    }
    @keyframes pp-count-pulse {
      0% { transform: scale(1); }
      50% { transform: scale(1.05); }
      100% { transform: scale(1); }
    }
    @keyframes pp-particle {
      0% { transform: translateY(0) translateX(0) scale(0); opacity: 0; }
      10% { opacity: 1; transform: scale(1); }
      90% { opacity: 0.6; }
      100% { transform: translateY(-100vh) translateX(var(--pp-drift)) scale(0.3); opacity: 0; }
    }
    @keyframes pp-slide-up {
      from { opacity: 0; transform: translateY(40px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes pp-gradient-shift {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
    .pp-card-hover { transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1); }
    .pp-card-hover:hover { transform: translateY(-8px); box-shadow: 0 20px 60px rgba(0,0,0,0.5) !important; }
    .pp-popular-card { animation: pp-glow-border 3s ease-in-out infinite; }
    .pp-cta-pulse { animation: pp-pulse 2s ease-in-out infinite; }
    .pp-nav-link { color: rgba(255,255,255,0.75); text-decoration: none; padding: 8px 16px; border-radius: 6px; transition: all 0.25s; font-size: 14px; font-weight: 500; white-space: nowrap; }
    .pp-nav-link:hover { color: #D4A03C; background: rgba(212,160,60,0.1); }
    .pp-section-animate { animation: pp-slide-up 0.8s ease-out both; }
    .pp-comparison-row { transition: all 0.3s; }
    .pp-comparison-row:hover { background: rgba(212,160,60,0.05) !important; }
    .pp-lang-btn { border: 1px solid rgba(255,255,255,0.2); background: transparent; color: rgba(255,255,255,0.8); padding: 4px 12px; border-radius: 4px; cursor: pointer; font-size: 13px; transition: all 0.2s; }
    .pp-lang-btn:hover, .pp-lang-btn.active { border-color: #D4A03C; color: #D4A03C; background: rgba(212,160,60,0.1); }
    @media (max-width: 768px) {
      .pp-nav-inner { display: none !important; }
      .pp-hero-stats { flex-direction: column; gap: 16px !important; }
      .pp-pricing-cards { flex-direction: column; align-items: center; }
      .pp-comparison-table { font-size: 13px; }
    }
  `;
  document.head.appendChild(style);
}

// ── Color palette ──────────────────────────────────────────────────
const C = {
  darkBg: '#060E1A',
  sectionBg: '#0A1F3F',
  cardBg: '#0D2847',
  cardBgLight: '#112F54',
  gold: '#D4A03C',
  goldLight: '#f0c866',
  goldDark: '#b8882e',
  white: '#FFFFFF',
  textPrimary: '#E8ECF1',
  textSecondary: '#8DA4BF',
  border: 'rgba(212,160,60,0.2)',
  gradientHero: 'linear-gradient(135deg, #060E1A 0%, #0A1F3F 40%, #0F2D52 70%, #0A1F3F 100%)',
};

// ── Particle background ────────────────────────────────────────────
function ParticlesBg() {
  const particles = Array.from({ length: 30 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    size: 2 + Math.random() * 4,
    duration: 8 + Math.random() * 12,
    delay: Math.random() * 10,
    drift: `${(Math.random() - 0.5) * 200}px`,
    opacity: 0.15 + Math.random() * 0.35,
  }));
  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0 }}>
      {particles.map(p => (
        <div key={p.id} style={{
          position: 'absolute', bottom: '-10px', left: p.left,
          width: p.size, height: p.size, borderRadius: '50%',
          background: `radial-gradient(circle, ${C.gold} 0%, transparent 70%)`,
          opacity: p.opacity,
          animation: `pp-particle ${p.duration}s linear ${p.delay}s infinite`,
          '--pp-drift': p.drift,
        }} />
      ))}
    </div>
  );
}

// ── Animated counter hook ──────────────────────────────────────────
function useCountUp(target, duration = 2000, trigger = true) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    if (!trigger) return;
    const start = performance.now();
    const step = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setVal(Math.round(eased * target));
      if (progress < 1) ref.current = requestAnimationFrame(step);
    };
    ref.current = requestAnimationFrame(step);
    return () => ref.current && cancelAnimationFrame(ref.current);
  }, [target, duration, trigger]);
  return val;
}

// ── Section wrapper with scroll reveal ─────────────────────────────
function Section({ id, children, style = {}, dark = false }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <section id={id} ref={ref} style={{
      padding: '80px 24px', maxWidth: 1200, margin: '0 auto',
      opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(40px)',
      transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
      ...style,
    }}>
      {children}
    </section>
  );
}

// ── Check icon ─────────────────────────────────────────────────────
function Check() {
  return <CheckCircleFilled style={{ color: C.gold, fontSize: 16, marginRight: 10, flexShrink: 0 }} />;
}

// ── Main Component ─────────────────────────────────────────────────
export default function PartnershipPage() {
  const [lang, setLang] = useState(() => localStorage.getItem('pp-lang') || 'en');
  const [annual, setAnnual] = useState(false);
  const [deals, setDeals] = useState(5);
  const [avgPrice, setAvgPrice] = useState(500000);
  const [calcTier, setCalcTier] = useState('popular');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [heroVisible, setHeroVisible] = useState(false);
  const [form] = Form.useForm();

  const t = translations[lang] || translations.en;

  useEffect(() => { injectStyles(); setHeroVisible(true); }, []);
  useEffect(() => { localStorage.setItem('pp-lang', lang); }, [lang]);

  // Smooth scroll
  const scrollTo = useCallback((id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // Pricing helpers
  const prices = { starter: 299, popular: 799, elite: 1499 };
  const getPrice = (tier) => annual ? prices[tier] * 10 : prices[tier];
  const fmtPrice = (n) => `$${n.toLocaleString()}`;

  // Calculator — revshare: 100K THB (~$2,800) base, 50% profit split
  const tierCommission = { starter: 0, popular: 0.03, elite: 0.02, revshare: 0.50 };
  const tierBase = { starter: 299, popular: 799, elite: 1499, revshare: 2800 };
  const tierLeadBonus = { starter: 50, popular: 0, elite: 0, revshare: 0 };
  const aiLeadsPerDeal = 8;
  const calcRevenue = deals * avgPrice;
  const calcCommission = calcRevenue * tierCommission[calcTier];
  const calcLeadBonus = tierLeadBonus[calcTier] * deals * aiLeadsPerDeal;
  const calcNetRevenue = calcTier === 'revshare'
    ? calcRevenue * 0.5 - tierBase[calcTier]
    : calcRevenue - tierBase[calcTier] + calcLeadBonus;
  const calcLeads = deals * aiLeadsPerDeal;
  const calcSavings = deals * 800;
  const calcROI = tierBase[calcTier] > 0 ? Math.round(((calcNetRevenue) / tierBase[calcTier]) * 100) : 0;

  // Animated stat counters
  const statPartners = useCountUp(500, 2500, heroVisible);
  const statCountries = useCountUp(30, 2000, heroVisible);
  const statSales = useCountUp(25, 2500, heroVisible);

  // Submit handler
  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const payload = {
        full_name: values.name,
        company_name: values.company,
        email: values.email,
        phone: values.phone,
        country: values.country,
        city: values.city,
        current_listings: values.listings,
        monthly_sales: values.monthly_sales,
        selected_tier: values.tier,
        message: values.message,
      };
      const res = await fetch(`${API_BASE}/partnership/inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('API error');
      setSubmitted(true);
      message.success(t.form.success);
    } catch {
      message.error(t.form.error);
    } finally {
      setSubmitting(false);
    }
  };

  // Tier card icons
  const tierIcons = {
    starter: <RocketOutlined style={{ fontSize: 32, color: C.gold }} />,
    popular: <ThunderboltOutlined style={{ fontSize: 32, color: C.gold }} />,
    elite: <CrownOutlined style={{ fontSize: 32, color: C.gold }} />,
  };

  const tierKeys = ['starter', 'popular', 'elite'];
  const allTierKeys = ['starter', 'popular', 'elite', 'revshare'];
  const tierLabels = {
    starter: t.pricing.tiers.starter.name,
    popular: t.pricing.tiers.popular.name,
    elite: t.pricing.tiers.elite.name,
    revshare: t.revshare.badge,
  };

  // Capability icons
  const capIcons = [
    <GlobalOutlined />, <VideoCameraOutlined />, <LineChartOutlined />,
    <SendOutlined />, <CustomerServiceOutlined />, <ShareAltOutlined />,
  ];

  // How-it-works icons
  const stepIcons = [
    <UserOutlined style={{ fontSize: 28, color: C.gold }} />,
    <RobotOutlined style={{ fontSize: 28, color: C.gold }} />,
    <BarChartOutlined style={{ fontSize: 28, color: C.gold }} />,
    <DollarOutlined style={{ fontSize: 28, color: C.gold }} />,
  ];

  return (
    <div style={{ background: C.darkBg, minHeight: '100vh', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", color: C.textPrimary }}>

      {/* ── Navigation Bar ── */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
        background: 'rgba(6,14,26,0.92)', backdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${C.border}`,
        padding: '0 24px', height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }} onClick={() => scrollTo('hero')}>
          <HomeOutlined style={{ fontSize: 22, color: C.gold }} />
          <span style={{ fontSize: 18, fontWeight: 700, color: C.white, letterSpacing: 1 }}>HOUSE AI</span>
        </div>
        <div className="pp-nav-inner" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {[
            ['how-it-works', t.nav.howItWorks],
            ['capabilities', t.nav.capabilities],
            ['calculator', t.nav.calculator],
            ['testimonials', t.nav.testimonials],
          ].map(([id, label]) => (
            <a key={id} className="pp-nav-link" href={`#${id}`} onClick={e => { e.preventDefault(); scrollTo(id); }}>{label}</a>
          ))}
          <Button type="primary" size="small" onClick={() => scrollTo('signup')}
            style={{ background: C.gold, borderColor: C.gold, color: '#000', fontWeight: 600, marginLeft: 8 }}>
            {t.nav.signUp}
          </Button>
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {[['en', 'EN'], ['zh', '繁中'], ['th', 'ไทย']].map(([key, label]) => (
            <button key={key} className={`pp-lang-btn ${lang === key ? 'active' : ''}`} onClick={() => setLang(key)}>{label}</button>
          ))}
        </div>
      </nav>

      {/* ── Hero Section ── */}
      <div id="hero" style={{
        position: 'relative', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: C.gradientHero, overflow: 'hidden', paddingTop: 60,
      }}>
        <ParticlesBg />
        <div style={{
          position: 'relative', zIndex: 1, textAlign: 'center', maxWidth: 900, padding: '40px 24px',
          opacity: heroVisible ? 1 : 0, transform: heroVisible ? 'translateY(0)' : 'translateY(30px)',
          transition: 'all 1s cubic-bezier(0.4, 0, 0.2, 1)',
        }}>
          <div style={{ marginBottom: 24 }}>
            <Tag style={{ background: 'rgba(212,160,60,0.15)', border: `1px solid ${C.gold}`, color: C.gold, fontSize: 13, padding: '4px 16px', fontWeight: 600 }}>
              <SafetyCertificateOutlined style={{ marginRight: 6 }} /> REVENUE-SHARING PARTNERSHIP
            </Tag>
          </div>
          <h1 style={{
            fontSize: 'clamp(36px, 6vw, 64px)', fontWeight: 800, lineHeight: 1.1, margin: '0 0 24px',
            background: `linear-gradient(135deg, ${C.white} 0%, ${C.gold} 50%, ${C.white} 100%)`,
            backgroundSize: '200% auto', animation: 'pp-shimmer 4s linear infinite',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
          }}>
            {t.hero.title}
          </h1>
          <p style={{ fontSize: 'clamp(16px, 2.5vw, 20px)', color: C.textSecondary, maxWidth: 700, margin: '0 auto 40px', lineHeight: 1.7 }}>
            {t.hero.subtitle}
          </p>
          <Button type="primary" size="large" className="pp-cta-pulse"
            onClick={() => scrollTo('signup')}
            style={{
              background: `linear-gradient(135deg, ${C.gold} 0%, ${C.goldLight} 100%)`,
              border: 'none', color: '#000', fontWeight: 700, fontSize: 18,
              height: 56, padding: '0 48px', borderRadius: 12,
            }}>
            {t.hero.cta} <ArrowRightOutlined />
          </Button>
          <div className="pp-hero-stats" style={{
            display: 'flex', justifyContent: 'center', gap: 48, marginTop: 64,
            padding: '32px 40px', background: 'rgba(255,255,255,0.04)',
            borderRadius: 16, border: `1px solid ${C.border}`, backdropFilter: 'blur(10px)',
          }}>
            {[
              { val: `${statPartners}+`, label: t.hero.stats.partners },
              { val: `${statCountries}+`, label: t.hero.stats.countries },
              { val: `$${statSales / 10}B+`, label: t.hero.stats.sales },
            ].map((s, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 800, color: C.gold, animation: 'pp-count-pulse 2s ease-in-out infinite' }}>{s.val}</div>
                <div style={{ fontSize: 14, color: C.textSecondary, marginTop: 4 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
        {/* Bottom gradient fade */}
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 120, background: `linear-gradient(to top, ${C.darkBg}, transparent)` }} />
      </div>

      {/* ── How It Works ── */}
      <div style={{ background: C.sectionBg }}>
        <Section id="how-it-works">
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 700, color: C.white, margin: 0 }}>{t.howItWorks.title}</h2>
            <p style={{ color: C.textSecondary, fontSize: 17, marginTop: 12 }}>{t.howItWorks.subtitle}</p>
          </div>
          <Row gutter={[32, 32]} justify="center">
            {t.howItWorks.steps.map((step, i) => (
              <Col xs={24} sm={12} md={6} key={i}>
                <div style={{ textAlign: 'center', padding: 24 }}>
                  <div style={{
                    width: 72, height: 72, borderRadius: '50%', margin: '0 auto 20px',
                    background: `linear-gradient(135deg, rgba(212,160,60,0.15), rgba(212,160,60,0.05))`,
                    border: `2px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {stepIcons[i]}
                  </div>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%', margin: '-36px auto 16px',
                    background: C.gold, color: '#000', fontWeight: 800, fontSize: 14,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative',
                  }}>{i + 1}</div>
                  <h3 style={{ color: C.white, fontSize: 18, fontWeight: 600, marginBottom: 8 }}>{step.title}</h3>
                  <p style={{ color: C.textSecondary, fontSize: 14, lineHeight: 1.6, margin: 0 }}>{step.desc}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Section>
      </div>

      {/* ── AI Capabilities ── */}
      <div style={{ background: C.darkBg }}>
        <Section id="capabilities">
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 700, color: C.white, margin: 0 }}>{t.capabilities.title}</h2>
            <p style={{ color: C.textSecondary, fontSize: 17, marginTop: 12 }}>{t.capabilities.subtitle}</p>
          </div>
          <Row gutter={[24, 24]}>
            {t.capabilities.items.map((item, i) => (
              <Col xs={24} sm={12} md={8} key={i}>
                <div className="pp-card-hover" style={{
                  padding: 32, borderRadius: 16, height: '100%',
                  background: `linear-gradient(145deg, ${C.cardBg}, ${C.cardBgLight})`,
                  border: `1px solid ${C.border}`,
                }}>
                  <div style={{
                    width: 52, height: 52, borderRadius: 12, marginBottom: 20,
                    background: 'rgba(212,160,60,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 24, color: C.gold,
                  }}>
                    {capIcons[i]}
                  </div>
                  <h3 style={{ color: C.white, fontSize: 17, fontWeight: 600, marginBottom: 10 }}>{item.title}</h3>
                  <p style={{ color: C.textSecondary, fontSize: 14, lineHeight: 1.7, margin: 0 }}>{item.desc}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Section>
      </div>

      {/* ── Pricing Section (hidden) ── */}
      {false && (
      <div style={{ background: `linear-gradient(180deg, ${C.sectionBg} 0%, ${C.darkBg} 100%)` }}>
        <Section id="pricing">
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 700, color: C.white, margin: 0 }}>{t.pricing.title}</h2>
            <p style={{ color: C.textSecondary, fontSize: 17, marginTop: 12 }}>{t.pricing.subtitle}</p>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 28 }}>
              <span style={{ color: !annual ? C.gold : C.textSecondary, fontWeight: 600, fontSize: 15 }}>{t.pricing.monthly}</span>
              <Switch checked={annual} onChange={setAnnual}
                style={{ background: annual ? C.gold : '#555' }}
              />
              <span style={{ color: annual ? C.gold : C.textSecondary, fontWeight: 600, fontSize: 15 }}>
                {t.pricing.annual}
                {annual && <Tag style={{ marginLeft: 8, background: 'rgba(212,160,60,0.15)', border: `1px solid ${C.gold}`, color: C.gold, fontSize: 11 }}>{t.pricing.annualSave}</Tag>}
              </span>
            </div>
          </div>

          <Row gutter={[24, 24]} justify="center" align="stretch" className="pp-pricing-cards">
            {tierKeys.map((tierKey) => {
              const tierData = t.pricing.tiers[tierKey];
              const isPopular = tierKey === 'popular';
              const isElite = tierKey === 'elite';
              return (
                <Col xs={24} md={8} key={tierKey} style={{ display: 'flex' }}>
                  <div
                    className={`pp-card-hover ${isPopular ? 'pp-popular-card' : ''}`}
                    style={{
                      flex: 1, borderRadius: 20, padding: isPopular ? '40px 28px' : '32px 28px',
                      background: isPopular
                        ? `linear-gradient(160deg, #102A4A 0%, #153560 50%, #102A4A 100%)`
                        : `linear-gradient(160deg, ${C.cardBg} 0%, ${C.cardBgLight} 100%)`,
                      border: isPopular ? `2px solid ${C.gold}` : `1px solid ${C.border}`,
                      position: 'relative', display: 'flex', flexDirection: 'column',
                      transform: isPopular ? 'scale(1.05)' : 'none',
                      zIndex: isPopular ? 2 : 1,
                    }}
                  >
                    {/* Badge */}
                    <div style={{ marginBottom: 20 }}>
                      <Tag style={{
                        background: isPopular ? `linear-gradient(135deg, ${C.gold}, ${C.goldLight})` : 'rgba(212,160,60,0.15)',
                        border: isPopular ? 'none' : `1px solid ${C.border}`,
                        color: isPopular ? '#000' : C.gold,
                        fontWeight: 700, fontSize: 12, padding: '3px 14px', letterSpacing: 1,
                      }}>
                        {isPopular ? <FireOutlined style={{ marginRight: 4 }} /> : null}
                        {tierData.badge}
                      </Tag>
                      {tierData.tag && (
                        <Tag style={{ marginLeft: 8, background: 'rgba(76,175,80,0.15)', border: '1px solid rgba(76,175,80,0.4)', color: '#66BB6A', fontSize: 11, padding: '2px 10px' }}>
                          {tierData.tag}
                        </Tag>
                      )}
                    </div>

                    {/* Icon + Name */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                      {tierIcons[tierKey]}
                      <h3 style={{ color: C.white, fontSize: 20, fontWeight: 700, margin: 0 }}>{tierData.name}</h3>
                    </div>

                    {/* Price */}
                    <div style={{ margin: '20px 0' }}>
                      <span style={{ fontSize: 44, fontWeight: 800, color: C.gold }}>{fmtPrice(getPrice(tierKey))}</span>
                      <span style={{ fontSize: 16, color: C.textSecondary, marginLeft: 4 }}>{annual ? (lang === 'en' ? '/yr' : lang === 'zh' ? '/年' : '/ปี') : t.pricing.perMonth}</span>
                      {annual && (
                        <div style={{ fontSize: 13, color: C.textSecondary, marginTop: 4, textDecoration: 'line-through' }}>
                          {fmtPrice(prices[tierKey] * 12)}{lang === 'en' ? '/yr' : lang === 'zh' ? '/年' : '/ปี'}
                        </div>
                      )}
                    </div>

                    {/* Best for */}
                    <p style={{ fontSize: 13, color: C.textSecondary, lineHeight: 1.5, marginBottom: 4 }}>
                      <strong style={{ color: C.gold }}>{t.pricing.bestFor}:</strong> {tierData.bestFor}
                    </p>
                    <p style={{ fontSize: 13, color: C.textSecondary, lineHeight: 1.5, marginBottom: 20 }}>
                      <strong style={{ color: C.gold }}>{t.pricing.revenueModel}:</strong> {tierData.revenue}
                    </p>

                    {/* Features */}
                    <div style={{ flex: 1 }}>
                      {tierData.features.map((f, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12, fontSize: 14, color: C.textPrimary, lineHeight: 1.5 }}>
                          <Check /><span>{f}</span>
                        </div>
                      ))}
                    </div>

                    {/* CTA */}
                    <Button
                      type="primary" block size="large"
                      className={isPopular ? 'pp-cta-pulse' : ''}
                      onClick={() => { form.setFieldsValue({ tier: tierKey }); scrollTo('signup'); }}
                      style={{
                        marginTop: 24, height: 48, borderRadius: 10, fontWeight: 700, fontSize: 15,
                        background: isPopular ? `linear-gradient(135deg, ${C.gold}, ${C.goldLight})` : 'transparent',
                        border: isPopular ? 'none' : `2px solid ${C.gold}`,
                        color: isPopular ? '#000' : C.gold,
                      }}
                    >
                      {isElite ? t.pricing.contactSales : t.pricing.getStarted} <ArrowRightOutlined />
                    </Button>
                  </div>
                </Col>
              );
            })}
          </Row>

          {/* ── Revenue Share Standalone Plan ── */}
          <div style={{
            marginTop: 64, padding: '48px 36px', borderRadius: 24,
            background: 'linear-gradient(160deg, #1a0a0a 0%, #2d1212 30%, #1a0505 100%)',
            border: '2px solid rgba(232,54,58,0.5)',
            position: 'relative', overflow: 'hidden',
          }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, transparent, #E8363A, #D4A03C, #E8363A, transparent)' }} />

            <div style={{ textAlign: 'center', marginBottom: 36 }}>
              <Tag style={{
                background: 'linear-gradient(135deg, #E8363A, #D4A03C)', border: 'none',
                color: '#fff', fontWeight: 700, fontSize: 14, padding: '6px 24px', letterSpacing: 2,
              }}>
                <SafetyCertificateOutlined style={{ marginRight: 6 }} />
                {t.revshare.badge}
              </Tag>
              <h2 style={{ fontSize: 'clamp(24px, 3.5vw, 36px)', fontWeight: 800, color: '#fff', margin: '16px 0 8px' }}>
                {t.revshare.title}
              </h2>
              <p style={{ color: C.textSecondary, fontSize: 17, maxWidth: 700, margin: '0 auto' }}>{t.revshare.subtitle}</p>

              <div style={{
                display: 'inline-flex', alignItems: 'baseline', gap: 6, marginTop: 24,
                padding: '12px 32px', borderRadius: 16,
                background: 'rgba(232,54,58,0.1)', border: '1px solid rgba(232,54,58,0.3)',
              }}>
                <span style={{ fontSize: 48, fontWeight: 800, color: '#E8363A' }}>100K</span>
                <span style={{ fontSize: 18, color: C.textSecondary }}>THB{t.pricing.perMonth}</span>
                <span style={{ fontSize: 18, color: C.textSecondary, margin: '0 8px' }}>+</span>
                <span style={{ fontSize: 48, fontWeight: 800, color: C.gold }}>50/50</span>
                <span style={{ fontSize: 18, color: C.textSecondary }}>{lang === 'en' ? 'profit split' : lang === 'zh' ? '利潤分潤' : 'แบ่งกำไร'}</span>
              </div>
            </div>

            <p style={{ color: C.textSecondary, fontSize: 15, lineHeight: 1.8, textAlign: 'center', maxWidth: 750, margin: '0 auto 36px' }}>
              {t.revshare.description}
            </p>

            {/* What you get */}
            <Row gutter={[24, 24]}>
              <Col xs={24} md={12}>
                <div style={{
                  background: 'rgba(255,255,255,0.04)', borderRadius: 16, padding: 24,
                  border: '1px solid rgba(212,160,60,0.2)', height: '100%',
                }}>
                  <h3 style={{ color: C.gold, fontSize: 18, fontWeight: 700, marginBottom: 16, margin: '0 0 16px' }}>
                    <CheckCircleFilled style={{ marginRight: 8 }} />{t.revshare.yourGet}
                  </h3>
                  {t.revshare.partnerGets.map((item, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12, fontSize: 14, color: C.textPrimary, lineHeight: 1.6 }}>
                      <Check /><span>{item}</span>
                    </div>
                  ))}
                </div>
              </Col>
              <Col xs={24} md={12}>
                <div style={{
                  background: 'rgba(232,54,58,0.06)', borderRadius: 16, padding: 24,
                  border: '1px solid rgba(232,54,58,0.2)', height: '100%',
                }}>
                  <h3 style={{ color: '#E8363A', fontSize: 18, fontWeight: 700, marginBottom: 16, margin: '0 0 16px' }}>
                    <SafetyCertificateOutlined style={{ marginRight: 8 }} />{t.revshare.protections}
                  </h3>
                  {t.revshare.protectionItems.map((item, i) => (
                    <div key={i} style={{
                      background: 'rgba(255,255,255,0.03)', borderRadius: 10, padding: '10px 14px',
                      marginBottom: 10, border: '1px solid rgba(255,255,255,0.06)',
                    }}>
                      <div style={{ color: '#fff', fontWeight: 600, fontSize: 14, marginBottom: 2 }}>{item.title}</div>
                      <div style={{ color: C.textSecondary, fontSize: 12, lineHeight: 1.5 }}>{item.desc}</div>
                    </div>
                  ))}
                </div>
              </Col>
            </Row>

            {/* CTA */}
            <div style={{ textAlign: 'center', marginTop: 32 }}>
              <Button
                type="primary" size="large"
                className="pp-cta-pulse"
                onClick={() => { form.setFieldsValue({ tier: 'revshare' }); scrollTo('signup'); }}
                style={{
                  height: 56, borderRadius: 14, fontWeight: 700, fontSize: 18,
                  padding: '0 48px',
                  background: 'linear-gradient(135deg, #E8363A, #D4A03C)',
                  border: 'none', color: '#fff',
                }}
              >
                {t.revshare.cta} <ArrowRightOutlined />
              </Button>
            </div>
          </div>
        </Section>
      </div>
      )}

      {/* ── Revenue Calculator ── */}
      <div style={{ background: C.darkBg }}>
        <Section id="calculator">
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 700, color: C.white, margin: 0 }}>{t.calculator.title}</h2>
            <p style={{ color: C.textSecondary, fontSize: 17, marginTop: 12 }}>{t.calculator.subtitle}</p>
          </div>
          <div style={{
            maxWidth: 800, margin: '0 auto', padding: 40, borderRadius: 20,
            background: `linear-gradient(145deg, ${C.cardBg}, ${C.cardBgLight})`,
            border: `1px solid ${C.border}`,
          }}>
            <Row gutter={[32, 32]}>
              <Col xs={24} md={12}>
                <div style={{ marginBottom: 28 }}>
                  <label style={{ color: C.textPrimary, fontSize: 14, fontWeight: 600, display: 'block', marginBottom: 10 }}>
                    {t.calculator.deals}: <span style={{ color: C.gold, fontWeight: 700 }}>{deals}</span>
                  </label>
                  <Slider min={1} max={30} value={deals} onChange={setDeals}
                    trackStyle={{ background: C.gold }} handleStyle={{ borderColor: C.gold, background: C.gold }}
                    railStyle={{ background: 'rgba(255,255,255,0.1)' }}
                  />
                </div>
                <div style={{ marginBottom: 28 }}>
                  <label style={{ color: C.textPrimary, fontSize: 14, fontWeight: 600, display: 'block', marginBottom: 10 }}>
                    {t.calculator.avgPrice}: <span style={{ color: C.gold, fontWeight: 700 }}>${avgPrice.toLocaleString()}</span>
                  </label>
                  <Slider min={100000} max={5000000} step={50000} value={avgPrice} onChange={setAvgPrice}
                    trackStyle={{ background: C.gold }} handleStyle={{ borderColor: C.gold, background: C.gold }}
                    railStyle={{ background: 'rgba(255,255,255,0.1)' }}
                  />
                </div>
                <div>
                  <label style={{ color: C.textPrimary, fontSize: 14, fontWeight: 600, display: 'block', marginBottom: 10 }}>{t.calculator.tier}</label>
                  <Radio.Group value={calcTier} onChange={e => setCalcTier(e.target.value)} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {allTierKeys.map(k => (
                      <Radio key={k} value={k} style={{ color: k === 'revshare' ? '#E8363A' : C.textPrimary, fontWeight: k === 'revshare' ? 700 : 400 }}>{tierLabels[k]}</Radio>
                    ))}
                  </Radio.Group>
                </div>
              </Col>
              <Col xs={24} md={12}>
                <Row gutter={[16, 20]}>
                  {[
                    { title: t.calculator.projected, value: `$${calcNetRevenue.toLocaleString()}`, color: C.gold },
                    { title: t.calculator.aiLeads, value: calcLeads, color: '#66BB6A' },
                    { title: t.calculator.costSavings, value: `$${calcSavings.toLocaleString()}`, color: '#42A5F5' },
                    { title: t.calculator.roi, value: `${calcROI}%`, color: '#AB47BC' },
                  ].map((stat, i) => (
                    <Col xs={12} key={i}>
                      <div style={{
                        padding: 20, borderRadius: 12, textAlign: 'center',
                        background: 'rgba(255,255,255,0.04)', border: `1px solid rgba(255,255,255,0.08)`,
                      }}>
                        <div style={{ fontSize: 28, fontWeight: 800, color: stat.color, marginBottom: 6 }}>{stat.value}</div>
                        <div style={{ fontSize: 12, color: C.textSecondary }}>{stat.title}</div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </Col>
            </Row>
          </div>
        </Section>
      </div>

      {/* ── ROI Comparison ── */}
      <div style={{ background: C.sectionBg }}>
        <Section id="comparison">
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 700, color: C.white, margin: 0 }}>{t.comparison.title}</h2>
            <p style={{ color: C.textSecondary, fontSize: 17, marginTop: 12 }}>{t.comparison.subtitle}</p>
          </div>
          <div className="pp-comparison-table" style={{ maxWidth: 750, margin: '0 auto', borderRadius: 16, overflow: 'hidden', border: `1px solid ${C.border}` }}>
            {/* Header */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', background: 'rgba(212,160,60,0.08)', padding: '16px 24px' }}>
              <div style={{ fontWeight: 700, color: C.white, fontSize: 15 }}></div>
              <div style={{ fontWeight: 700, color: '#ef5350', fontSize: 14, textAlign: 'center' }}>
                <CloseOutlined style={{ marginRight: 6 }} />{t.comparison.without}
              </div>
              <div style={{ fontWeight: 700, color: '#66BB6A', fontSize: 14, textAlign: 'center' }}>
                <CheckOutlined style={{ marginRight: 6 }} />{t.comparison.with}
              </div>
            </div>
            {t.comparison.metrics.map((m, i) => (
              <div key={i} className="pp-comparison-row" style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', padding: '14px 24px',
                background: i % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent',
                borderTop: `1px solid rgba(255,255,255,0.05)`,
              }}>
                <div style={{ color: C.textPrimary, fontWeight: 500, fontSize: 14 }}>{m.label}</div>
                <div style={{ color: '#ef5350', textAlign: 'center', fontSize: 14 }}>{m.without}</div>
                <div style={{ color: '#66BB6A', textAlign: 'center', fontSize: 14, fontWeight: 600 }}>{m.with}</div>
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* ── Testimonials ── */}
      <div style={{ background: C.darkBg }}>
        <Section id="testimonials">
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 700, color: C.white, margin: 0 }}>{t.testimonials.title}</h2>
            <p style={{ color: C.textSecondary, fontSize: 17, marginTop: 12 }}>{t.testimonials.subtitle}</p>
          </div>
          <Row gutter={[24, 24]}>
            {t.testimonials.items.map((item, i) => (
              <Col xs={24} md={8} key={i}>
                <div className="pp-card-hover" style={{
                  padding: 32, borderRadius: 16, height: '100%',
                  background: `linear-gradient(145deg, ${C.cardBg}, ${C.cardBgLight})`,
                  border: `1px solid ${C.border}`,
                  display: 'flex', flexDirection: 'column',
                }}>
                  <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
                    {[1,2,3,4,5].map(s => <StarFilled key={s} style={{ color: C.gold, fontSize: 16 }} />)}
                  </div>
                  <p style={{ color: C.textPrimary, fontSize: 14, lineHeight: 1.8, fontStyle: 'italic', flex: 1, margin: 0 }}>
                    "{item.quote}"
                  </p>
                  <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{
                        width: 44, height: 44, borderRadius: '50%',
                        background: `linear-gradient(135deg, ${C.gold}, ${C.goldDark})`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#000', fontWeight: 700, fontSize: 16,
                      }}>
                        {item.name.charAt(0)}
                      </div>
                      <div>
                        <div style={{ color: C.white, fontWeight: 600, fontSize: 14 }}>{item.name}</div>
                        <div style={{ color: C.textSecondary, fontSize: 12 }}>{item.role}</div>
                        <Tag style={{ marginTop: 4, fontSize: 11, background: 'rgba(212,160,60,0.1)', border: `1px solid ${C.border}`, color: C.gold }}>{item.country}</Tag>
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Section>
      </div>

      {/* ── Sign-Up Form ── */}
      <div style={{ background: `linear-gradient(180deg, ${C.sectionBg} 0%, ${C.darkBg} 100%)` }}>
        <Section id="signup">
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 700, color: C.white, margin: 0 }}>{t.form.title}</h2>
            <p style={{ color: C.textSecondary, fontSize: 17, marginTop: 12 }}>{t.form.subtitle}</p>
          </div>
          {submitted ? (
            <div style={{
              textAlign: 'center', padding: 60,
              background: `linear-gradient(145deg, ${C.cardBg}, ${C.cardBgLight})`,
              borderRadius: 20, border: `1px solid ${C.border}`, maxWidth: 600, margin: '0 auto',
            }}>
              <CheckCircleFilled style={{ fontSize: 64, color: '#66BB6A', marginBottom: 24 }} />
              <h3 style={{ color: C.white, fontSize: 24, fontWeight: 700 }}>{t.form.success}</h3>
              <p style={{ color: C.textSecondary, fontSize: 16, marginTop: 12 }}>ai@comboware.ai</p>
            </div>
          ) : (
            <div style={{
              maxWidth: 720, margin: '0 auto', padding: '40px 36px',
              background: `linear-gradient(145deg, ${C.cardBg}, ${C.cardBgLight})`,
              borderRadius: 20, border: `1px solid ${C.border}`,
            }}>
              <Form form={form} layout="vertical" onFinish={handleSubmit} requiredMark={false} initialValues={{ tier: 'popular' }}>
                <Row gutter={[20, 0]}>
                  <Col xs={24} sm={12}>
                    <Form.Item name="name" label={<span style={{ color: C.textPrimary, fontWeight: 500 }}>{t.form.name}</span>} rules={[{ required: true }]}>
                      <Input placeholder={t.form.namePh} size="large" prefix={<UserOutlined style={{ color: C.textSecondary }} />}
                        style={{ background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.12)', color: C.white, borderRadius: 8 }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="company" label={<span style={{ color: C.textPrimary, fontWeight: 500 }}>{t.form.company}</span>} rules={[{ required: true }]}>
                      <Input placeholder={t.form.companyPh} size="large" prefix={<HomeOutlined style={{ color: C.textSecondary }} />}
                        style={{ background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.12)', color: C.white, borderRadius: 8 }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="email" label={<span style={{ color: C.textPrimary, fontWeight: 500 }}>{t.form.email}</span>} rules={[{ required: true, type: 'email' }]}>
                      <Input placeholder={t.form.emailPh} size="large" prefix={<MailOutlined style={{ color: C.textSecondary }} />}
                        style={{ background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.12)', color: C.white, borderRadius: 8 }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="phone" label={<span style={{ color: C.textPrimary, fontWeight: 500 }}>{t.form.phone}</span>}>
                      <Input placeholder={t.form.phonePh} size="large" prefix={<PhoneOutlined style={{ color: C.textSecondary }} />}
                        style={{ background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.12)', color: C.white, borderRadius: 8 }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="country" label={<span style={{ color: C.textPrimary, fontWeight: 500 }}>{t.form.country}</span>} rules={[{ required: true }]}>
                      <Select placeholder={t.form.country} size="large" showSearch
                        style={{ width: '100%' }}
                        dropdownStyle={{ background: C.cardBg, borderColor: C.border }}
                        popupClassName="pp-select-dropdown"
                      >
                        {countries.map(c => <Option key={c} value={c}>{c}</Option>)}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="city" label={<span style={{ color: C.textPrimary, fontWeight: 500 }}>{t.form.city}</span>}>
                      <Input placeholder={t.form.cityPh} size="large" prefix={<EnvironmentOutlined style={{ color: C.textSecondary }} />}
                        style={{ background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.12)', color: C.white, borderRadius: 8 }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="listings" label={<span style={{ color: C.textPrimary, fontWeight: 500 }}>{t.form.listings}</span>}>
                      <Input placeholder={t.form.listingsPh} size="large" type="number"
                        style={{ background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.12)', color: C.white, borderRadius: 8 }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="monthly_sales" label={<span style={{ color: C.textPrimary, fontWeight: 500 }}>{t.form.monthlySales}</span>}>
                      <Input placeholder={t.form.monthlySalesPh} size="large" type="number"
                        style={{ background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.12)', color: C.white, borderRadius: 8 }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item name="tier" label={<span style={{ color: C.textPrimary, fontWeight: 500 }}>{t.form.tier}</span>} rules={[{ required: true }]}>
                      <Radio.Group style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        {allTierKeys.map(k => (
                          <Radio.Button key={k} value={k} style={{
                            background: k === 'revshare' ? 'rgba(232,54,58,0.12)' : 'rgba(255,255,255,0.06)',
                            borderColor: k === 'revshare' ? 'rgba(232,54,58,0.4)' : 'rgba(255,255,255,0.15)',
                            color: k === 'revshare' ? '#E8363A' : C.textPrimary,
                            borderRadius: 8, height: 40, lineHeight: '38px', padding: '0 20px',
                            fontWeight: k === 'revshare' ? 700 : 400,
                          }}>
                            {tierLabels[k]}
                          </Radio.Button>
                        ))}
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item name="message" label={<span style={{ color: C.textPrimary, fontWeight: 500 }}>{t.form.message}</span>}>
                      <TextArea rows={4} placeholder={t.form.messagePh}
                        style={{ background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.12)', color: C.white, borderRadius: 8 }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Button type="primary" htmlType="submit" block size="large" loading={submitting}
                      className="pp-cta-pulse"
                      style={{
                        height: 52, borderRadius: 10, fontWeight: 700, fontSize: 16,
                        background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`,
                        border: 'none', color: '#000',
                      }}>
                      {t.form.submit} <SendOutlined />
                    </Button>
                  </Col>
                </Row>
              </Form>
            </div>
          )}
        </Section>
      </div>

      {/* ── Footer ── */}
      <footer style={{
        background: 'rgba(6,14,26,0.95)', borderTop: `1px solid ${C.border}`,
        padding: '40px 24px', textAlign: 'center',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 12 }}>
          <HomeOutlined style={{ fontSize: 20, color: C.gold }} />
          <span style={{ fontSize: 18, fontWeight: 700, color: C.white, letterSpacing: 1 }}>HOUSE AI</span>
        </div>
        <p style={{ color: C.textSecondary, fontSize: 14, margin: '8px 0 4px' }}>{t.footer.company}</p>
        <p style={{ color: C.textSecondary, fontSize: 13, margin: '0 0 4px' }}>{t.footer.location}</p>
        <a href="mailto:ai@comboware.ai" style={{ color: C.gold, fontSize: 14 }}>ai@comboware.ai</a>
        <p style={{ color: C.textSecondary, fontSize: 14, marginTop: 16, fontStyle: 'italic' }}>{t.footer.tagline}</p>
        <Divider style={{ borderColor: C.border, margin: '20px 0' }} />
        <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, margin: 0 }}>
          &copy; {new Date().getFullYear()} {t.footer.company} {t.footer.rights}
        </p>
      </footer>
    </div>
  );
}
